import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';

import { UserService } from '../../providers/user.service';
import { AuthService } from '../../providers/auth.service';
import { AgreementService } from '../../providers/agreement.service';
import { User } from '../../models/user';
import { Person } from '../../models/person';
import { Agreement_version } from '../../models/agreement_version';
import { Usef_person } from '../../models/usef/usef_person';
import { SmsAbstractAppPage } from '../sms-abstract-app-page';
import { ModalUsefPersonSearchComponent } from '../people/modal-usef-person-search/modal-usef-person-search.component';
import { AppSettings } from '../../app.settings';

import {
  DIAL_CODE_OPTIONS,
  DialCodeOption,
  getDialCodeForCountry,
} from '../../utils/phone.utils';

@Component({
  standalone: false,
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.css']
})
export class CreateUserComponent extends SmsAbstractAppPage implements OnInit, OnDestroy {

  // ── User-facing state ──────────────────────────────────────────────────
  user: User = new User({});
  person: Person = new Person({});
  password_match: string = '';
  operation_pending: boolean = false;
  showPassword: boolean = false;
  showConfirm: boolean = false;

  // ── Step rail state ────────────────────────────────────────────────────
  accountCreated: boolean = false;

  // ── Phone (NANP / WhatsApp) ────────────────────────────────────────────
  phoneType: 'nanp' | 'whatsapp' = 'nanp';
  phoneDialCodeCountry: string = 'US';
  phoneLocal: string = '';

  readonly dialCodeOptions: DialCodeOption[] = DIAL_CODE_OPTIONS;
  get pinnedDialCodeOptions(): DialCodeOption[] { return this.dialCodeOptions.filter(o => o.pinned); }
  get otherDialCodeOptions(): DialCodeOption[]  { return this.dialCodeOptions.filter(o => !o.pinned); }

  get phoneDialCode(): string {
    return this.phoneType === 'nanp' ? '+1' : getDialCodeForCountry(this.phoneDialCodeCountry);
  }
  get phoneE164(): string {
    const digits = this.phoneLocal.replace(/\D/g, '');
    return digits ? this.phoneDialCode + digits : '';
  }
  get isValidNanpPhone(): boolean { return this.phoneLocal.replace(/\D/g, '').length === 10; }
  get isValidWhatsAppPhone(): boolean {
    const d = this.phoneLocal.replace(/\D/g, '');
    return d.length >= 4 && d.length <= 15;
  }

  // ── Email & SMS verification state ─────────────────────────────────────
  emailVerificationSent: boolean = false;
  emailCode: string = '';
  emailVerified: boolean = false;
  emailVerificationToken: string = '';

  phoneVerificationSent: boolean = false;
  smsCode: string = '';
  phoneVerified: boolean = false;
  phoneVerificationToken: string = '';

  verificationPending: boolean = false;

  get isValidEmail(): boolean { return /^.+@.+\..+$/.test((this.person.email || '').trim()); }

  /**
   * Email verification is required — every transaction receipt is delivered
   * by email, so a working address is the user's only proof of registration.
   * Phone verification is encouraged but optional.
   */
  get canCreateAccount(): boolean {
    return this.emailVerified;
  }

  // ── Username availability ──────────────────────────────────────────────
  usernameStatus: 'idle' | 'checking' | 'available' | 'taken' | 'error' = 'idle';
  private usernameInput$ = new Subject<string>();
  private usernameSub: Subscription;

  // ── Agreement clickwrap ───────────────────────────────────────────────
  acceptedPrivacyPolicy: boolean = false;
  acceptedUserAgreement: boolean = false;
  privacyPolicyUrl: string = '/review-agreements';
  userAgreementUrl: string = '/review-agreements';
  get agreementsAccepted(): boolean {
    return this.acceptedPrivacyPolicy && this.acceptedUserAgreement;
  }

  // ── Password strength ──────────────────────────────────────────────────
  get passwordStrength(): number {
    const p = this.user.password || '';
    let score = 0;
    if (p.length >= 8) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^a-zA-Z0-9]/.test(p)) score++;
    return score;
  }

  get pwHasLength(): boolean  { return (this.user.password || '').length >= 8; }
  get pwHasLower(): boolean   { return /[a-z]/.test(this.user.password || ''); }
  get pwHasUpper(): boolean   { return /[A-Z]/.test(this.user.password || ''); }
  get pwHasDigit(): boolean   { return /[0-9]/.test(this.user.password || ''); }
  get pwHasSpecial(): boolean { return /[^a-zA-Z0-9]/.test(this.user.password || ''); }

  get passwordValid(): boolean { return this.passwordStrength >= 4; }
  get passwordsMatch(): boolean {
    return !!this.password_match && this.user.password === this.password_match;
  }

  // ── Step status helpers (drive the step rail) ──────────────────────────
  get contactComplete(): boolean {
    return !!this.person.first_name?.trim()
        && !!this.person.last_name?.trim()
        && this.isValidEmail
        && this.canCreateAccount;
  }

  get credentialsComplete(): boolean {
    return !!this.user.username
        && this.usernameStatus !== 'taken'
        && this.passwordValid
        && this.passwordsMatch;
  }

  constructor(
    private modal: NgbModal,
    private userService: UserService,
    private authService: AuthService,
    private agreementService: AgreementService,
    private router: Router,
  ) {
    super();
  }

  ngOnInit() {
    // Resolve current document URLs so the clickwrap links point at the
    // actual PDFs the user is being asked to accept. If this fails, the
    // links fall back to the in-app review page.
    this.agreementService.getCurrentVersions().then(versions => {
      for (const v of versions as Agreement_version[]) {
        if (v.agreement_type === 'privacy_policy') this.privacyPolicyUrl = v.document_url;
        if (v.agreement_type === 'user_agreement') this.userAgreementUrl = v.document_url;
      }
    }).catch(() => { /* fall back to /review-agreements links */ });

    // Live username availability — debounced HEAD-style check.
    this.usernameSub = this.usernameInput$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(username => {
        if (!username || username.length < 4) {
          this.usernameStatus = 'idle';
          return of(null);
        }
        if (!/^[a-zA-Z0-9_\-]+$/.test(username)) {
          this.usernameStatus = 'idle';
          return of(null);
        }
        this.usernameStatus = 'checking';
        return this.userService.checkUsername(username).pipe(
          catchError(() => { this.usernameStatus = 'error'; return of(null); })
        );
      })
    ).subscribe(result => {
      if (result === null) return;
      this.usernameStatus = result ? 'available' : 'taken';
    });
  }

  ngOnDestroy() {
    this.usernameSub?.unsubscribe();
  }

  // ── Phone ────────────────────────────────────────────────────────────
  setPhoneType(type: 'nanp' | 'whatsapp') {
    this.phoneType = type;
    this.phoneLocal = '';
    this.phoneDialCodeCountry = 'US';
    this.resetPhoneVerification();
  }

  onPhoneLocalChange() { this.resetPhoneVerification(); }

  private resetPhoneVerification() {
    this.phoneVerified = false;
    this.phoneVerificationToken = '';
    this.phoneVerificationSent = false;
    this.smsCode = '';
  }

  sendSmsVerification() {
    this.verificationPending = true;
    this.userService.sendVerification('sms', this.phoneE164)
      .then(() => {
        this.phoneVerificationSent = true;
        this.verificationPending = false;
      })
      .catch(error => {
        this.handleError(error);
        this.verificationPending = false;
      });
  }

  verifySmsCode() {
    if (!this.smsCode?.trim()) return;
    this.verificationPending = true;
    this.userService.verifyCode('sms', this.phoneE164, this.smsCode.trim())
      .then(result => {
        this.phoneVerified = true;
        this.phoneVerificationToken = result.token;
        this.verificationPending = false;
      })
      .catch(error => {
        this.handleError(error);
        this.phoneVerified = false;
        this.verificationPending = false;
      });
  }

  // ── Email ────────────────────────────────────────────────────────────
  onEmailChange() {
    this.emailVerified = false;
    this.emailVerificationToken = '';
    this.emailVerificationSent = false;
    this.emailCode = '';
  }

  sendEmailVerification() {
    this.verificationPending = true;
    this.userService.sendVerification('email', (this.person.email || '').trim())
      .then(() => {
        this.emailVerificationSent = true;
        this.verificationPending = false;
      })
      .catch(error => {
        this.handleError(error);
        this.verificationPending = false;
      });
  }

  verifyEmailCode() {
    if (!this.emailCode?.trim()) return;
    this.verificationPending = true;
    this.userService.verifyCode('email', (this.person.email || '').trim(), this.emailCode.trim())
      .then(result => {
        this.emailVerified = true;
        this.emailVerificationToken = result.token;
        this.verificationPending = false;
      })
      .catch(error => {
        this.handleError(error);
        this.emailVerified = false;
        this.verificationPending = false;
      });
  }

  // ── Username ─────────────────────────────────────────────────────────
  onUsernameChange(value: string) { this.usernameInput$.next(value); }

  // ── Submit (create account) ──────────────────────────────────────────
  onSubmit() {
    try {
      if (!this.contactComplete) {
        throw new Error('Please complete your contact details and verify your email address.');
      }
      if (this.usernameStatus === 'taken') {
        throw new Error('Please choose an available username.');
      }
      if (!this.passwordValid) {
        throw new Error('Password does not meet the strength requirements.');
      }
      if (!this.passwordsMatch) {
        throw new Error('Passwords do not match.');
      }
      if (!this.agreementsAccepted) {
        throw new Error('Please review and accept the Privacy Policy and User Agreement.');
      }

      this.operation_pending = true;

      // Populate person record minimally — no address. The user fills those
      // in later when they enter themselves as owner/trainer/rider/payee.
      this.person.phone = this.phoneVerified ? this.phoneE164 : (this.person.phone || '');

      const verification_tokens = {
        email: this.emailVerified ? this.emailVerificationToken : null,
        sms:   this.phoneVerified ? this.phoneVerificationToken : null,
      };

      this.userService.createUser(this.user, this.person, this.password_match, verification_tokens)
        .then(createdUser => {
          if (!createdUser || !createdUser.id) {
            this.operation_pending = false;
            this.handleError('Account creation failed. Please try again.');
            return;
          }
          // Log the new user in so the USEF link step can authenticate.
          this.authService.login(createdUser.username, this.user.password)
            .then(login_response => {
              if (login_response === true) {
                // Record clickwrap assent against the current versions. If this
                // fails, the AuthGuard will catch the user on next navigation
                // and route them to /review-agreements — so we don't block here.
                this.agreementService
                  .recordAssent(['privacy_policy', 'user_agreement'])
                  .catch(() => { /* swallow — AuthGuard is the safety net */ })
                  .then(() => {
                    this.accountCreated = true;
                    this.operation_pending = false;
                    setTimeout(() => {
                      document.getElementById('cu-usef-link')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 150);
                  });
              } else {
                this.handleError(login_response);
                this.operation_pending = false;
              }
            })
            .catch(error => {
              this.handleError(error);
              this.operation_pending = false;
            });
        })
        .catch(error => {
          this.handleError(error);
          this.operation_pending = false;
        });
    } catch (error) {
      this.handleError(error);
      this.operation_pending = false;
    }
  }

  // ── USEF link (post-account) ─────────────────────────────────────────
  handleUSEFSearch() {
    if (!this.accountCreated) return;
    const modal_ref = this.modal.open(ModalUsefPersonSearchComponent, { size: 'lg' });
    modal_ref.componentInstance.person_selected$.subscribe(
      (usef_person: Usef_person) => {
        modal_ref.close();
        this.userService.linkUsefMembership(usef_person.id, usef_person.uuid, usef_person.entity_type)
          .then(() => this.router.navigate(['home']))
          .catch(error => this.handleError(error));
      },
      error => this.handleError(error)
    );
  }

  handleSkipUsefLink() { this.router.navigate(['home']); }
}
